import { useState, useEffect, useMemo } from 'react'
import api from '../../services/api'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import Button from '../../components/common/Button'
import Modal from '../../components/common/Modal'
import CategoryTabs from './components/CategoryTabs'
import DocumentTable from './components/DocumentTable'
import DocumentForm from './components/DocumentForm'
import './Documents.css'

interface Media {
  id: number
  file_name: string
  file_path: string
  mime_type: string
  file_size: number
  alt_text?: string
  title?: string
}

interface Document {
  id: number
  title: string
  description: string
  media_id?: number
  media?: Media
  download_url?: string
  category: string
  position: number
  featured: boolean
  status: 'draft' | 'published'
  created_at: string
}

function Documents() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeCategory, setActiveCategory] = useState('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDocument, setEditingDocument] = useState<Document | null>(null)

  useEffect(() => {
    fetchDocuments()
  }, [])

  const fetchDocuments = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await api.get<Document[]>('/documents')
      setDocuments(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  // Memoize filtered and sorted documents
  const filteredDocuments = useMemo(() => {
    if (!documents || documents.length === 0) return []

    let filtered = documents

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = documents.filter((doc) => doc.category === activeCategory)
    }

    // Sort by position (create new array, don't mutate)
    return [...filtered].sort((a, b) => a.position - b.position)
  }, [documents, activeCategory])

  const getCategoryCounts = () => {
    const counts: Record<string, number> = {
      all: documents.length,
      flyers: 0,
      programme: 0,
      communiques: 0,
      autres: 0,
    }

    documents.forEach((doc) => {
      if (counts[doc.category] !== undefined) {
        counts[doc.category]++
      }
    })

    return counts
  }

  const handleCreate = () => {
    setEditingDocument(null)
    setIsModalOpen(true)
  }

  const handleEdit = (doc: Document) => {
    setEditingDocument(doc)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/documents/${id}`)
      setDocuments(documents.filter((doc) => doc.id !== id))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  const handleSubmit = async (docData: Omit<Document, 'id' | 'created_at'>) => {
    try {
      if (editingDocument) {
        // Update existing document
        const updated = await api.put<Document>(`/documents/${editingDocument.id}`, docData)
        setDocuments(documents.map((doc) => (doc.id === editingDocument.id ? updated : doc)))
      } else {
        // Create new document
        const created = await api.post<Document>('/documents', docData)
        setDocuments([...documents, created])
      }
      setIsModalOpen(false)
      setEditingDocument(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setEditingDocument(null)
  }

  if (loading) return <Loading />
  if (error) return <ErrorMessage message={error} />

  return (
    <div className="documents-module">
      <div className="module-header">
        <h1>Gestion des Documents</h1>
        <Button onClick={handleCreate}>Nouveau Document</Button>
      </div>

      <CategoryTabs
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        counts={getCategoryCounts()}
      />

      <div className="documents-content">
        {filteredDocuments.length === 0 ? (
          <div className="empty-state">
            <p>Aucun document dans cette catégorie</p>
            <Button onClick={handleCreate}>Créer le premier document</Button>
          </div>
        ) : (
          <DocumentTable
            documents={filteredDocuments}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCancel}
        title={editingDocument ? 'Modifier le Document' : 'Nouveau Document'}
      >
        <DocumentForm
          document={editingDocument}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Modal>
    </div>
  )
}

export default Documents
